import { ForgotPasswordProps, ResetPasswordProps } from '../screens/Modals/ModalProps';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createResetRequest, logIn, resetPassword, userOtpLogin } from '../services/auth.api';
import { getStorageValue, removeStorageValue, setStorageValue } from '../utils/cookies';

import { InitialAuthType } from '../types/auth';
import KEYS from '../consts/consts';
import type { RootState } from '../store';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import { openModal } from './modal.slice';
import { toast } from 'react-toastify';
import {
  FORCE_RESET,
  SUCCESS_LOGIN,
  USER_ID_INVALID,
  VERIFY_OTP,
} from "../utils/responseCodes";

const accessToken = getStorageValue("access_token") || "";
const user_id = getStorageValue("user_id") || "";

const initialState: InitialAuthType = {
  result: null,
  responseCode: null,
  access_token: accessToken,
  otpToken: null,
  isLoggedIn: !isEmpty(accessToken),
  userConfigData: {},
  resetResponse: {},
  isForGotPwdModalOpen: false,
  isResendDisabled: false,
  ssoDetails: null,
  loading: false,
  isButtonDisabled: true,
  isLinkExpired: false,
  userId: user_id,
  forgotPasswordForm: null,
};
interface CustomError extends Error {
  response?: any;
}

export const fetchLogin = createAsyncThunk(
  "user/login",
  async ({ loginReqDetails, navigate, toast }: any) => {
    try {
      const response = await logIn(loginReqDetails);
      const {
        data: {
          qrCodeUrl = "",
          resetPasswordToken = "",
          token = "",
          otpToken = "",
          responseCode = "",
        },
      } = response;

      if (responseCode === FORCE_RESET) {
        navigate(`/reset-password?token=${resetPasswordToken}`);
      } else if (responseCode === VERIFY_OTP) {
        navigate(`/otp-login?token=${otpToken}`, {
          state: { qrCode: qrCodeUrl },
        });
      } else if (responseCode === USER_ID_INVALID) {
        toast.error("Couldn't find your email id. Please check and try again.");
      } else if (responseCode === SUCCESS_LOGIN) {
        setStorageValue("access_token", token);
        setStorageValue("user_id", response.data.userId);
        setStorageValue("userRole", response.data.userRole);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        toast.success(KEYS.LOGIN_SUCESS);
        navigate("/landlords");
      }
      else {
        toast.error(
          "Wrong password. Try again or click Forgot Password to reset it."
        );
      }

      return response?.data;
    } catch (error) {
      const err = error as CustomError;
      if (err) {
        const errMsg =
          err.response.status === 401
            ? err.response.data
            : KEYS.ERROR_OCCURED_TRY_AGAIN;
        toast.error("Invalid email or password");
        return errMsg;
      }
    }
  }
);

export const sendUserOtpLogin = createAsyncThunk('user/send/otp', async ({ finalData, navigate, toast }: any) => {
  const response = await userOtpLogin(finalData);
  const { data = {} } = response;
  const { token = '', userId,userName } = data;
  if(response.data.responseCode=="2011")
    toast.error(response.data.message);
  else if (!isEmpty(data)) {
    setStorageValue('access_token', token);
    setStorageValue('user_id', userId);
    setStorageValue('username',userName);
    setStorageValue('userRole',response.data.userRole);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    toast.success(KEYS.LOGIN_SUCESS);
    navigate('/landlords');
  } else {
    toast.error(KEYS.ENTER_OTP_AGAIN);
  }
  return data;
});

export const sendResetRequest = createAsyncThunk('User/reset-password-request', async (data: any, { dispatch }) => {
  const response = await createResetRequest(data);
  dispatch(openModal(ForgotPasswordProps));
  return response?.data;
});

export const sendResetPassword = createAsyncThunk('user/reset-password', async (data: any, { dispatch }) => {
  const response = await resetPassword(data);
  dispatch(openModal(ResetPasswordProps));
  return response?.data;
});

export const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: state => {
      document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
      sessionStorage.clear();
      localStorage.removeItem('token'); 
      removeStorageValue('access_token');
      removeStorageValue('user_id');
      state.isLoggedIn = false;
      state.access_token = '';
      state.otpToken = '';
      axios.defaults.headers.common.Authorization = '';
    },
    setIsForGotPwdModalOpen: (state, action) => {
      state.isForGotPwdModalOpen = action.payload;
    },
    setIsResendDisabled: (state, action) => {
      state.isResendDisabled = action.payload;
    },
    setIsButtonDisabled: (state, action) => {
      state.isButtonDisabled = action.payload;
    },
    setIsLinkExpired: (state, action) => {
      state.isLinkExpired = action.payload;
    },
  },
  
  extraReducers: builder => {
    builder
      .addCase(fetchLogin.fulfilled, (state, action) => {
        const { otpToken, userId } = action.payload;
        state.otpToken = otpToken;
        state.isLoggedIn = !isEmpty(otpToken);
        state.userId = userId;
      })

      .addCase(sendUserOtpLogin.fulfilled, (state, action) => {
        const { userId } = action.payload;
        state.isResendDisabled = true;
        state.userId = userId;
      })

      .addCase(sendResetRequest.pending, state => {
        state.loading = true;
      })
      .addCase(sendResetRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.isResendDisabled = true;
        state.forgotPasswordForm = action.meta.arg;
      })
      .addCase(sendResetRequest.rejected, state => {
        state.loading = false;
        toast.error('Invalid email!');
      })

      .addCase(sendResetPassword.pending, state => {
        state.loading = true;
      })
      .addCase(sendResetPassword.fulfilled, state => {
        state.loading = false;
      })
      .addCase(sendResetPassword.rejected, state => {
        state.loading = false;
        state.isLinkExpired = true;
        toast.error('Link expired');
      });
  },
});

export const { logout, setIsForGotPwdModalOpen, setIsResendDisabled, setIsButtonDisabled } =
  AuthSlice.actions;

export const selectAuth = (state: RootState) => state.auth || {};
export const selectAccessToken = (state: RootState) => state.auth.access_token;

export default AuthSlice.reducer;
